import { useState, useEffect } from 'react';
import { Star, MessageSquare, MessageCircle, Camera, Heart, User, Phone, Wine, Users, Package, Award } from 'lucide-react';
import nhost from '../nhost';

const ACTIVITY_META = {
  rate_wine:       { label: 'Betygsatt vin',             Icon: Star,            points: 5 },
  comment_wine:    { label: 'Kommenterat vin',            Icon: MessageSquare,   points: 2 },
  session_comment: { label: 'Provningskommentar',         Icon: MessageCircle,   points: 3 },
  upload_photo:    { label: 'Uppladdad bild',             Icon: Camera,          points: 5 },
  wine_interest:   { label: 'Markerat intresse för vin',  Icon: Heart,           points: 1 },
  profile_avatar:  { label: 'Profilbild uppladdad',       Icon: User,            points: 10 },
  profile_phone:   { label: 'Telefonnummer sparat',       Icon: Phone,           points: 5 },
  host_session:    { label: 'Hållit provning',            Icon: Wine,            points: 20 },
  attend_session:  { label: 'Deltagit i provning',        Icon: Users,           points: 10 },
  winebox_rsvp:    { label: 'Accepterat vinlåda',         Icon: Package,         points: 3 },
};

const cardStyle = {
  background: '#fff',
  border: '1px solid rgba(60,20,30,.08)',
  borderRadius: 16,
  marginBottom: 12,
};

const divider = { borderTop: '1px solid rgba(60,20,30,.07)' };

const sectionTitle = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 1.5,
  textTransform: 'uppercase',
  color: 'var(--muted)',
  marginBottom: 10,
};

export default function PointsBreakdown({ userId, displayName, totalPoints, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    nhost.graphql.request(
      `query($uid: uuid!) { user_points(where: { user_id: { _eq: $uid } }) { activity points } }`,
      { uid: userId }
    ).then(({ data }) => {
      if (!data?.user_points) return;
      const grouped = {};
      data.user_points.forEach(p => {
        if (!grouped[p.activity]) grouped[p.activity] = { count: 0, total: 0 };
        grouped[p.activity].count += 1;
        grouped[p.activity].total += p.points;
      });
      const result = Object.entries(grouped).map(([activity, { count, total }]) => ({
        activity,
        count,
        total,
        ...ACTIVITY_META[activity],
      })).sort((a, b) => b.total - a.total);
      setRows(result);
      setLoading(false);
    });
  }, [userId]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--cream)', zIndex: 200, overflowY: 'auto' }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <div style={{
          display: 'flex', alignItems: 'center',
          padding: '10px 16px', background: 'var(--cream)',
          position: 'sticky', top: 0, zIndex: 10,
          borderBottom: '1px solid rgba(60,20,30,.06)',
        }}>
          <button
            onClick={onClose}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--wine)', fontSize: 14, fontWeight: 500, padding: '6px 0' }}
          >
            ← Tillbaka
          </button>
        </div>

        <div style={{ padding: '16px 16px 100px' }}>
          <div style={{ ...cardStyle, background: 'var(--wine-light)', border: 'none', borderRadius: 18, padding: 20, textAlign: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, marginBottom: 4 }}>
              <Award size={16} color="var(--wine)" strokeWidth={1.7} />
              <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--wine)' }}>Totala poäng</span>
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 40, fontWeight: 600, color: 'var(--wine)', lineHeight: 1 }}>{totalPoints}p</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{displayName}s poänghistorik</div>
          </div>

          <div style={{ ...sectionTitle, marginBottom: 8 }}>Poäng per aktivitet</div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)', fontSize: 13 }}>Laddar...</div>
          ) : (
            <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
              {rows.map((r, i) => {
                const Icon = r.Icon || Award;
                return (
                  <div key={r.activity} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 16px',
                    ...(i < rows.length - 1 ? divider : {}),
                  }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--wine-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={16} strokeWidth={1.7} color="var(--wine)" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{r.label || r.activity}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                        {r.count} {r.count === 1 ? 'gång' : 'gånger'} × {r.points}p
                      </div>
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--wine)', flexShrink: 0 }}>{r.total}p</div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ ...sectionTitle, marginTop: 16, marginBottom: 8 }}>Så tjänar du poäng</div>
          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            {Object.entries(ACTIVITY_META).map(([key, { Icon, label, points }], i, arr) => (
              <div key={key} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 16px',
                ...(i < arr.length - 1 ? divider : {}),
              }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={15} strokeWidth={1.7} color="var(--muted)" />
                </div>
                <div style={{ flex: 1, fontSize: 13, color: 'var(--ink)' }}>{label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--wine)', flexShrink: 0 }}>+{points}p</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
